<template>
	<page-container class="index" :page="page">
		<heading-image ref="header">
			<hero-image v-if="page.headingImage" type="fullscreen">
				<div v-if="defaults[locale].homepage.headingImageTitle" class="hero-content">
					<p>
						<span>{{ defaults[locale].homepage.headingImageTitle }}</span>
					</p>
					<div v-parse-links v-html="defaults[locale].homepage.headingImageContent ?? ''" />
				</div>

				<div class="slider-overlay" />
				<picture>
					<source
						v-if="page.headingImagePortraitWebp"
						:srcset="page.headingImagePortraitWebp"
						media="(max-width: 500px)"
						type="image/webp"
					/>
					<source
						v-if="page.headingImagePortrait"
						:srcset="page.headingImagePortrait"
						media="(max-width: 500px)"
					/>
					<source :srcset="page.headingImageWebp" type="image/webp" />
					<source :srcset="page.headingImage" />
					<img class="object-fit" :src="page.headingImage" :alt="page.headingImageAlt" />
				</picture>
			</hero-image>

			<booking-form />
			<scroll-down :header-height="header?.height" />
		</heading-image>

		<main-content>
			<div class="row bg-image-container">
				<div class="bg-image-right" />
				<div class="columns column6 intro-image">
					<picture>
						<source :srcset="defaults[locale].homepage.introImageWebp" type="image/webp" />
						<source :srcset="defaults[locale].homepage.introImage" />
						<img
							:src="defaults[locale].homepage.introImage"
							:alt="defaults[locale].homepage.introImageAlt"
							loading="lazy"
						/>
					</picture>
				</div>
				<div class="columns column5 intro-content">
					<h2 class="subtitle">{{ page.subtitle }}</h2>
					<h1>{{ page.title }}</h1>
					<div v-parse-links v-html="page.content" />
				</div>
			</div>
		</main-content>

		<mosaic :items="mosaicData" />

		<contentblocks :items="page.contentblocks">
			<template #blocks-header>
				<div class="bg-image-container">
					<div class="bg-image-left" />
					<div class="row blocks-header white align-center">
						<div class="columns column12">
							<h2 class="subtitle">
								{{ defaults[locale].homepage.sectionContentblocksSubtitle }}
							</h2>
							<h2>
								{{ defaults[locale].homepage.sectionContentblocksTitle }}
							</h2>
							<div v-parse-links v-html="defaults[locale].homepage.sectionContentblocksContent ?? ''" />
						</div>
					</div>
				</div>
			</template>
		</contentblocks>

		<promoblocks :items="promoblocksData">
			<template #blocks-header>
				<div class="row promoblocks blocks-header align-center">
					<div class="columns column12">
						<h2 class="subtitle">
							{{ defaults[locale].homepage.sectionPromotionSubtitle }}
						</h2>
						<h2>
							{{ defaults[locale].homepage.sectionPromotionTitle }}
						</h2>
						<div v-parse-links v-html="defaults[locale].homepage.sectionPromotionContent ?? ''" />
					</div>
				</div>
			</template>
		</promoblocks>

		<frequently-asked-questions
			v-if="page.frequentlyAskedQuestion"
			:items="page.frequentlyAskedQuestion.faqitems"
			:header="page.frequentlyAskedQuestion.header"
		/>
	</page-container>
</template>

<script setup>
import { ref } from 'vue';

const header = ref(null);
const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

const { data: mosaicData } = await useWebsiteFetch('mosaic', {
	query: { language: locale.value },
	key: `${locale.value}/mosaic`,
});

const { data: promoblocksData } = await useWebsiteFetch('promoblocks', {
	query: { language: locale.value },
	key: `${locale.value}/promoblocks`,
});
</script>

<style lang="scss" scoped>
.slider-overlay {
	position: absolute;
	inset: 0;
	z-index: 2;
	background: rgb(0 0 0 / 25%);
}

.hero-content {
	position: absolute;
	max-width: 1400px;
	z-index: 10;
	left: 0;
	right: 0;
	width: auto;
	bottom: 50%;
	margin: 0 auto -70px;
	text-align: center;
	color: #fff;
	letter-spacing: 2px;

	:deep(span) {
		color: #fff;
		font-size: 56px;
		font-family: var(--heading-font-family);
		font-style: italic;
		font-weight: 400;
		margin: 0 0 10px;
		width: 60%;
		display: inline-block;
		line-height: 68px;
	}

	:deep(p) {
		font-size: 1.8em;
	}
}

.hero-image {
	img {
		animation: zoom-in 11000ms ease-in-out forwards;
	}
}

.bg-image-container {
	position: relative;
}

.bg-image-right {
	background-image: url('~/assets/images/debrinkvectorright.svg');
	background-repeat: no-repeat;
	background-size: 80%;
	background-position: top left;
	position: absolute;
	left: 0;
	height: 500px;
	width: 120%;
	margin-left: 30%;
	margin-top: -30px;
	overflow: hidden;
	scrollbar-gutter: stable;
}

.bg-image-left {
	background-image: url('~/assets/images/debrinkvectorleft.svg');
	background-repeat: no-repeat;
	background-size: 70%;
	background-position: top right;
	position: absolute;
	top: 0;
	right: 0;
	width: 100%;
	height: 500px;
	margin-top: 180px;
	margin-right: 30%;
	overflow: hidden;

	.row {
		margin-top: 10px;
		padding-bottom: 0;
	}
}

.intro-image picture img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.intro-image {
	padding: 0 7px;
	z-index: 1;
}

.intro-content {
	display: flex;
	flex-flow: column;
	margin-left: 80px;
	justify-content: flex-end;
	gap: 10px;
	z-index: 1;

	:deep(p) {
		margin: 15px 0 0;
	}
}

.promotions,
.contentblocks {
	h2 {
		color: var(--header-color);
	}

	:deep(p) {
		color: var(--body-color);
	}

	.subtitle {
		display: flex;
		justify-content: center;
	}

	.subtitle::after {
		content: '';
		display: block;
		width: 30px;
		height: 1px;
		background-color: #122d1d;
		position: relative;
		flex-shrink: 0;
	}
}

.promotions {
	background: var(--secondary-background-color);

	:deep(h2) {
		margin: 0 auto 40px;
	}

	:deep(p) {
		margin: 0 auto;
		width: 100%;
		font-size: 18px;
		line-height: 34px;
	}
}

@media (max-width: 1480px) {
	h1 {
		font-size: 38px;
	}

	.hero-content {
		:deep(span) {
			font-size: 48px;
			line-height: 60px;
			width: 50%;
		}
	}

	.subtitle {
		font-size: 15px !important;
	}

	.promotions {
		h2 {
			font-size: 36px;
		}

		:deep(p) {
			font-size: 15px;
			line-height: 30px;
		}
	}

	.promoblocks.blocks-header {
		margin-bottom: 50px;
	}
}

@media (max-width: 1230px) {
	.intro-content,
	.intro-image {
		margin-left: 0 !important;
		width: 100% !important;
		margin-top: 30px;
	}

	.bg-image-left {
		margin-bottom: 50px;
	}
}

@media (max-width: 880px) {
	.hero-content {
		:deep(span) {
			font-size: 32px;
			line-height: 45px;
			font-weight: 400;
			width: 80%;
		}

		:deep(p) {
			font-size: 1.6em;
			padding: 0 10px;
		}
	}

	.bg-image-right {
		margin-top: 10px;
	}

	.bg-image-left {
		width: 140%;
		margin-right: 10px;
	}

	.promotions {
		h2 {
			font-size: 32px;
		}
	}

	h1 {
		font-size: 32px;
	}
}
</style>
